import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { ShoppingCart, CheckCircle, XCircle, Clock, FileText, Mail, User, Euro } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResourceOrdersPage({ admin, onLogout }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/resource-orders`);
      setOrders(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/resource-orders/${orderId}/status`, { status });
      fetchOrders();
    } catch (e) {
      alert('Erreur lors de la mise à jour');
    }
  };

  const filtered = filter === 'all' ? orders : orders.filter((o) => o.status === filter);

  const statusBadge = (status) => {
    const map = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'En attente', icon: Clock },
      confirmed: { bg: 'bg-green-100', text: 'text-green-700', label: 'Confirmée', icon: CheckCircle },
      cancelled: { bg: 'bg-red-100', text: 'text-red-700', label: 'Annulée', icon: XCircle },
    };
    const b = map[status] || map.pending;
    const Icon = b.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${b.bg} ${b.text}`}>
        <Icon size={12} />
        {b.label}
      </span>
    );
  };

  return (
    <AdminLayout admin={admin} onLogout={onLogout} title="Commandes de Ressources">
      <div className="space-y-6">
        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          {[
            ['all', 'Toutes'],
            ['pending', 'En attente'],
            ['confirmed', 'Confirmées'],
            ['cancelled', 'Annulées'],
          ].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setFilter(val)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === val
                  ? 'bg-violet-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-violet-50'
              }`}
              data-testid={`filter-orders-${val}`}
            >
              {label} ({val === 'all' ? orders.length : orders.filter((o) => o.status === val).length})
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-violet-100">
            <ShoppingCart className="mx-auto text-gray-300 mb-4" size={64} />
            <p className="text-gray-500">Aucune commande à afficher.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((o) => (
              <div
                key={o.id}
                className="bg-white rounded-xl p-6 shadow-md border border-violet-100"
                data-testid={`order-${o.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="p-2 bg-violet-100 rounded-lg">
                        <FileText className="text-violet-600" size={24} />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-800">
                          {o.resource?.title || 'Ressource supprimée'}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1">Réf. {o.reference} · {o.payment_method === 'card' ? 'Carte' : 'Virement'}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600 mt-3">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-violet-500" />
                        <span>
                          {o.learner?.first_name} {o.learner?.last_name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail size={14} className="text-violet-500" />
                        <span className="truncate">{o.learner?.email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Euro size={14} className="text-violet-500" />
                        <span className="font-semibold">
                          {Number(o.amount).toFixed(2).replace('.', ',')} €
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        Commandé le {new Date(o.created_at).toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    {statusBadge(o.status)}
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100 flex flex-wrap gap-2">
                  {o.status !== 'confirmed' && (
                    <button
                      onClick={() => updateStatus(o.id, 'confirmed')}
                      className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors text-sm font-semibold"
                      data-testid={`confirm-order-${o.id}`}
                    >
                      <CheckCircle size={16} />
                      Confirmer le paiement
                    </button>
                  )}
                  {o.status !== 'cancelled' && (
                    <button
                      onClick={() => updateStatus(o.id, 'cancelled')}
                      className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-sm"
                      data-testid={`cancel-order-${o.id}`}
                    >
                      <XCircle size={16} />
                      Annuler
                    </button>
                  )}
                  {o.status === 'confirmed' && (
                    <span className="text-xs text-gray-500 self-center ml-auto">
                      ✅ L'apprenant peut télécharger la ressource dans son espace
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
